import { isPossiblePhoneNumber } from 'libphonenumber-js';

import type { AccountManager, User } from './_types';
import type { Client } from 'src/models/client/_types';
import type { Session } from 'src/models/session/_types';
import type { Permissions } from 'src/shared/usePermissionCheck';

export type { AccountManager, User } from './_types';

export const hasValidPhoneNumber = (
  phoneNumber: string | null | undefined,
  country?: string | null
): boolean => {
  const value = phoneNumber || '';
  if (!value) return false;

  const defaultCountry = (country || 'US') as Parameters<
    typeof isPossiblePhoneNumber
  >[1];

  return (
    isPossiblePhoneNumber(value) ||
    isPossiblePhoneNumber(value, defaultCountry)
  );
};

export const getUserDisplayName = (
  user?: User | Client,
  label: string = 'Unknown'
): string => {
  if (!user) return label;
  if (user.firstName || user.lastName) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  if ('email' in user && user.email) {
    return user.email;
  }
  if ('clientName' in user && user.clientName) {
    return user.clientName;
  }
  if ('nickname' in user && user.nickname) {
    return user.nickname;
  }
  return label;
};

export const isHeadTrainer = (user?: User): boolean => {
  return !!user && user.trainerId === user.id;
};

export const isSubTrainer = (user?: User): boolean => {
  return !!user && !!user.headTrainerId;
};

export const isProTrainer = (user?: User | null): boolean => {
  return !!user && (user.payingTrainer || !!user.headTrainerId);
};

export const isManager = (user?: User | null): boolean => {
  return !!user?.roles?.includes('manager');
};

export const isAdmin = (user?: User | null): boolean => {
  return !!user?.roles?.includes('admin') || !!user?.roles?.includes('super_admin') || !!user?.roles?.includes('migration') || !!user?.roles?.includes('support');
};

export const isSuperAdmin = (user?: User | null): boolean => {
  return !!user?.roles?.includes('super_admin');
};

export const isSupportRole = (user?: User | null): boolean => {
  return !!user?.roles?.includes('support');
};

export const isMigrationRole = (user?: User | null): boolean => {
  return !!user?.roles?.includes('migration');
};

export const isTrainer = (user?: User): boolean =>
  !!user && (isHeadTrainer(user) || isSubTrainer(user));

export const getUserInitials = (user: User): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
  }
  if (user.nickname) {
    return user.nickname.charAt(0);
  }
  if (user.email) {
    return user.email.charAt(0);
  }
  return '00';
};

export const getDependentInitials = (
  accountManager: AccountManager
): string => {
  const parts = (accountManager.dependentName || '').split(' ').filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`;
  }
  if (accountManager.dependentEmail) {
    return accountManager.dependentEmail.charAt(0);
  }
  return '00';
};

export const getDependentDisplayName = (
  accountManager?: AccountManager
): string => {
  if (!accountManager) return 'Unknown';
  if (accountManager.dependentName) return accountManager.dependentName;
  if (accountManager.dependentEmail) return accountManager.dependentEmail;
  return 'Unknown';
};

export const getUserAddress = (user: User): string => {
  const addressParts = [
    user.streetAddress,
    user.city,
    user.state,
    user.zipCode,
  ];
  const nonEmptyParts = addressParts.filter(
    (part) => !!part && String(part).trim() !== ''
  );
  return nonEmptyParts.join(', ');
};

export const getClientAutocompleteKey = (
  user: User,
  permissions: Permissions
): string => {
  if (permissions.canOnlyManageLocationClient && !!user.locationId) {
    return `client_location_${user.locationId}`;
  }
  if (permissions.canManageClientsForTrainer) {
    return `client_trainer_${user.trainerId}`;
  }
  return `client_trainer_${user.id}`;
};

export const defaultStats = [
  { stat: 'weight', label: 'Weight', value: 'weight' },
  { stat: 'height', label: 'Height', value: 'height' },
  { stat: 'bmi', label: 'BMI', value: 'bmi' },
  { stat: 'systolic_bp', label: 'Systolic BP', value: 'systolic_bp' },
  { stat: 'diastolic_bp', label: 'Diastolic BP', value: 'diastolic_bp' },
  { stat: 'body_fat_', label: 'Body Fat %', value: 'body_fat_' },
  { stat: 'rhr', label: 'Resting Heart Rate', value: 'rhr' },
  { stat: 'waist', label: 'Waist Size', value: 'waist' },
  { stat: 'thigh', label: 'Thigh Size', value: 'thigh' },
  { stat: 'bicep', label: 'Bicep Size', value: 'bicep' },
  {
    stat: 'blood_pressure',
    label: 'Blood Pressure',
    value: 'blood_pressure',
  },
];

export const statsForPlatform = (
  platform: Session
): {
  stat: string;
  label: string;
  value: string | null;
  metric?: string | null;
}[] => {
  const stats: {
    stat: string;
    label: string;
    value: string | null;
    metric?: string | null;
  }[] = [];
  stats.push(...defaultStats);

  if (platform.healthInfoMetrics) {
    stats.push(
      ...(platform.healthInfoMetrics as string[]).map((stat) => {
        return { stat: stat, label: stat, value: stat };
      })
    );
  }

  // defaultHealthMetrics are the stats we *DON'T* want to include
  return stats.filter((r) => !platform.defaultHealthMetrics?.includes(r.label));
};

/**
 * Check if a user's birthday is within the next N days
 * Matches v2 user-alert-icons component logic (lines 33-62)
 *
 * @param birthdate - ISO date string of the user's birthdate
 * @param withinDays - Number of days to check (default 7)
 * @returns Birthday message if within range, null otherwise
 */
export const getBirthdayMessage = (
  birthdate: string | null,
  withinDays = 7
): string | null => {
  if (!birthdate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const nextDate = new Date(today);
  nextDate.setDate(today.getDate() + withinDays);

  // Get this year's birthday
  const birthdateObj = new Date(birthdate);
  const thisYearBirthday = new Date(
    today.getFullYear(),
    birthdateObj.getMonth(),
    birthdateObj.getDate()
  );
  thisYearBirthday.setHours(0, 0, 0, 0);

  if (thisYearBirthday >= today && thisYearBirthday <= nextDate) {
    return `Birthday is coming up on ${thisYearBirthday.toLocaleDateString(
      'en-US',
      { month: 'long', day: 'numeric' }
    )}!`;
  }

  return null;
};

/**
 * Compares two version strings to check if currentVersion >= targetVersion
 * @param currentVersion - Current version string (e.g., "5.10.0")
 * @param targetVersion - Target version to compare against (e.g., "5.10.0")
 * @returns true if currentVersion >= targetVersion
 */
export const versionAtLeast = (
  currentVersion: string | undefined,
  targetVersion: string
): boolean => {
  if (!currentVersion) return false;

  const currentParts = currentVersion.split('.').map((part) => parseInt(part));
  const targetParts = targetVersion.split('.').map((part) => parseInt(part));

  for (let i = 0; i < targetParts.length; i++) {
    const current = currentParts[i] || 0;
    const target = targetParts[i] || 0;

    if (current > target) return true;
    if (current < target) return false;
  }

  return true;
};

/**
 * Checks if the user's app version supports SMS functionality
 * SMS is supported in app version 5.10.0 and above (iOS or Android)
 * @param user - User object with currentIosVersion and currentAndroidVersion
 * @returns true if either iOS or Android version supports SMS
 */
export const supportsSms = (
  user?: Pick<User, 'currentIosVersion' | 'currentAndroidVersion'>
): boolean => {
  if (!user) return false;
  return (
    versionAtLeast(user.currentIosVersion, '5.10.0') ||
    versionAtLeast(user.currentAndroidVersion, '5.10.0')
  );
};
